// src/components/landing/RotatingImages.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";

interface RotatingImageProps {
  images: string[]; // Array of image paths
  className: string; // CSS class for positioning and styling (e.g., "shapes screen-one av-icons")
  width: number; // Width prop for Next.js Image
  height: number; // Height prop for Next.js Image
  style: React.CSSProperties; // Inline style for width percentage
  intervalRange?: [number, number]; // Optional tuple for min and max interval in milliseconds
}

export default function RotatingImages({
  images,
  className,
  width,
  height,
  style,
  intervalRange = [4000, 15000], // Default range: Xs to Xs
}: RotatingImageProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Function to generate a random interval within the specified range
  const getRandomInterval = useCallback(() => {
    const [min, max] = intervalRange;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }, [intervalRange]);

  useEffect(() => {
    // Set initial random interval
    let intervalId: NodeJS.Timeout;
    const setNewInterval = () => {
      clearInterval(intervalId); // Clear any existing interval
      const interval = getRandomInterval();
      intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, interval);
      return intervalId;
    };

    setNewInterval(); // Set the first interval

    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, [images.length, getRandomInterval]); // Re-run if images or intervalRange changes

  useEffect(() => {
  }, [currentIndex, images.length]); // Re-run whenever currentIndex changes

  return (
    <>
      {images.map((src, index) => (
        <Image
          key={src}
          src={src}
          alt=""
          className={`${className} ${index === currentIndex ? "fade-in" : "fade-out"}`}
          width={width}
          height={height}
          style={{
            ...style,
            position: "absolute",
            opacity: index === currentIndex ? 1 : 0,
            transition: "opacity 0.8s ease-in-out",
          }}
        />
      ))}
    </>
  );
}